import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useState } from 'react';

import { useLogger } from '@/hooks/useLogger';
import { Document } from '@/models/Document';
import { createDocuments as createDocumentsEndpoint } from '@/services/api/documents/api';
import { generatePreSignedUrls as generatePreSignedUrlsEndpoint } from '@/services/api/files';

export const useFileUploadWithPresigning = (
  isExtractable: boolean | undefined,
  propertyId: string,
) => {
  const logger = useLogger('useFileUploadWithPresigning');

  const [uploadProgress, setUploadProgress] = useState(0);
  const [inProgress, setInProgress] = useState(false);
  const [isSuccess, setIsSuccess] = useState<boolean>();
  const [result, setResult] = useState<Document[]>([]);

  const { mutateAsync: generatePreSignedUrlsAsync } = useMutation({
    mutationFn: (requestBody: {
      files: { filename: string; mimeType: string; isExtractable: boolean }[];
    }) => generatePreSignedUrlsEndpoint(requestBody),
  });

  const { mutateAsync: createDocumentsAsync } = useMutation({
    mutationFn: (requestBody: {
      propertyId: string;
      documents: { filename: string; fileSize: number; mimeType: string; s3Key: string }[];
    }) => createDocumentsEndpoint(requestBody),
  });

  const handleStartFileUpload = (files: File[]) => {
    setUploadProgress(0);
    setInProgress(true);
    setIsSuccess(undefined);
    setResult([]);

    const uploadFilesAsync = async () => {
      try {
        const uploadedDocuments = await uploadFiles(files, isExtractable ?? true);

        setUploadProgress(99);

        setTimeout(() => {
          setUploadProgress(100);
          setIsSuccess(true);
          setResult(uploadedDocuments);
          setInProgress(false);
        }, 500);
      } catch (error) {
        setIsSuccess(false);
        logger.error('file upload failed', { error });
        setInProgress(false);
      }
    };

    uploadFilesAsync();
  };

  const updateProgress = (percent: number) => {
    const percentCompleted = Math.min(99, percent);

    if (percentCompleted > uploadProgress) {
      setUploadProgress(percentCompleted);
    }
  };

  const uploadFiles = async (files: File[], isExtractable: boolean) => {
    logger.debug('generating presigned urls, files:', files);
    const preSignedUrls = await generatePreSignedUrlsAsync({
      files: files.map((file) => ({
        filename: file.name,
        mimeType: file.type,
        isExtractable,
      })),
    });
    logger.debug('generated presigned urls:', preSignedUrls);

    logger.debug('uploading files to s3');
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    let totalUploaded = 0;

    const uploadPromises = preSignedUrls.map(async ({ filename, mimeType, preSignedUrl }) => {
      const file = files.find((f) => f.name === filename);
      if (!file) return;

      await axios.put(preSignedUrl, file, {
        headers: {
          'Content-Type': mimeType,
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const fileProgress = progressEvent.loaded / progressEvent.total;
            totalUploaded += (fileProgress * file.size) / files.length;
            const overallPercent = Math.round((totalUploaded / totalSize) * 100);
            updateProgress(overallPercent);
          }
        },
      });
    });
    await Promise.all(uploadPromises);
    logger.debug('uploaded files to s3');

    logger.debug('creating documents in db');
    const documents = await createDocumentsAsync({
      propertyId,
      documents: preSignedUrls.map(({ filename, mimeType, key: s3Key }) => ({
        filename,
        fileSize: files.find((f) => f.name === filename)?.size ?? 0,
        mimeType,
        s3Key,
      })),
    });
    logger.debug('created documents in db:', documents);

    return documents;
  };

  return {
    startFileUpload: handleStartFileUpload,
    uploadProgress,
    inProgress,
    isSuccess,
    result,
  };
};
